import React from 'react';
import { CalendarDays, MapPin, Repeat } from 'lucide-react';
import { CalendarCard } from '../../types';
import { requestOpenDetail } from '../../services/calendarService';
import { SCOPE_STYLE } from '../calendar/scopeStyles';

interface CalendarEventCardProps {
  card: CalendarCard;
  messageId: number | string;
  darkMode?: boolean;
  /** 전달된 카드면 원 발신자 이름 — 말풍선 대신 카드가 그려지므로 라벨을 카드가 단다 */
  forwardedFrom?: string | null;
}

/**
 * 대화방 일정 공유 카드 — 말풍선 자리에 그린다(본문은 구버전 클라이언트용 대체 텍스트라 숨긴다).
 *
 * MessageItem이 직접 import한다(링크 미리보기 카드와 같은 이유 — 콜백 prop을 늘리면 memo 계약 표면이 커진다).
 * 누르면 calendarService.requestOpenDetail로 App 루트의 상세 창을 연다. 공유 메시지를 읽을 수 있는 사람은
 * 그 일정을 읽기 전용으로 볼 수 있다(`?via=messageId` — 서버가 메시지 접근권을 다시 판정한다).
 *
 * ⚠ z-*를 붙이지 말 것 — 선택 모드 오버레이(absolute inset-0 z-10)를 뚫는다.
 * ⚠ 분류색은 scopeStyles의 정적 클래스만 쓴다. 브랜드 레드는 쓰지 않는다(액션·경고로 읽힌다).
 */
const CalendarEventCardComponent: React.FC<CalendarEventCardProps> = ({ card, messageId, darkMode, forwardedFrom }) => {
  const surface = darkMode
    ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
    : 'bg-white border-slate-200 hover:bg-slate-50';

  if (card.deleted || card.unavailable) {
    // 삭제 카드의 제목은 서버가 행이 남아 있을 때만 싣는다(90일 뒤 purge·실시간 전파에는 없다 — 그때는 클라가 가진 값)
    const deletedTitle = card.deleted && card.title ? card.title : '';
    return (
      <div className={`w-[260px] sm:w-[300px] max-w-full rounded-xl border px-3 py-2.5 text-sm ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
        {deletedTitle ? (
          <div className="space-y-1">
            <span className="flex items-center gap-1.5 text-xs">
              <CalendarDays size={13} aria-hidden="true" />
              {forwardedFrom ? `전달됨 · ${forwardedFrom} · ` : ''}삭제된 일정
            </span>
            <div className={`line-clamp-1 break-anywhere line-through ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>{deletedTitle}</div>
          </div>
        ) : (
          <span className="flex items-center gap-1.5">
            <CalendarDays size={15} aria-hidden="true" />
            {card.deleted ? '삭제된 일정입니다' : '일정 정보를 불러오지 못했습니다'}
          </span>
        )}
      </div>
    );
  }

  const style = SCOPE_STYLE[card.scope || 'personal'];
  const open = () => requestOpenDetail({ eventId: card.id, date: card.date, viaMessageId: messageId });

  return (
    <button
      type="button"
      onClick={open}
      data-calendar-card
      className={`block w-[260px] sm:w-[300px] max-w-full text-left overflow-hidden rounded-xl border border-l-4 ${style.bar} shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${surface}`}
    >
      <div className="px-3 py-2.5 space-y-1">
        <div className={`flex items-center gap-1.5 text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
          <CalendarDays size={13} aria-hidden="true" />
          <span className="truncate">
            {forwardedFrom ? `전달됨 · ${forwardedFrom} · ` : ''}{style.label === '내 일정' ? '일정' : `${style.label} 일정`}
          </span>
        </div>
        <div className={`text-sm font-semibold line-clamp-2 break-anywhere ${darkMode ? 'text-gray-100' : 'text-slate-800'}`}>
          {card.title}
        </div>
        <div className={`text-[13px] ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>
          {card.ended ? `${card.when} (지난 일정)` : card.when}
        </div>
        {card.repeat && card.repeatSummary && (
          <div className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
            <Repeat size={12} aria-hidden="true" />
            <span className="truncate">{card.repeatSummary}</span>
          </div>
        )}
        {card.location && (
          <div className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
            <MapPin size={12} aria-hidden="true" />
            <span className="truncate">{card.location}</span>
          </div>
        )}
      </div>
    </button>
  );
};

export const CalendarEventCard = React.memo(CalendarEventCardComponent);
